// ============================================================
// システムデータ整合 — 対象フィールドのホワイトリスト（2026-07-28）
//
// このファイルが「何を読めて・何を書き換えられるか」の唯一の定義。
// AI（Gemini）が返したフィールド名は必ずここを通して検証する（未知の名前は捨てる）。
//
// ⚠️ sys/reconcile/system_api.php の $RS_FIELDS / $RS_EDITABLE と対になっている。
//    片方だけ変更しないこと（AI が知らないフィールドを提案する／その逆になる）。
//
// editable の方針（2026-07-28 ユーザー決定・第1弾）:
//   **非価格フィールドのみ書き換え可**。価格系（sum_price / base_price / …）は
//   抽出の条件には使えるが書き換え禁止。2026-05 の 580 件破損事象が価格系だったため、
//   運用実績を作ってから次フェーズで解禁する。
//   （解禁するときはここの editable を true にするだけでよい設計にしてある）
// ============================================================

/** 値の置き場所。書き込み時のペイロード構築が変わる */
export type FieldPath = 'root' | 'detail';

export type FieldKind = 'string' | 'number' | 'flag';

export interface FieldDef {
  key: string;
  label: string;
  path: FieldPath;
  kind: FieldKind;
  /** 一括編集の書き込み先にできるか */
  editable: boolean;
  /** 価格系（誤操作の影響が最も大きい群）。UI で警告表示に使う */
  price?: boolean;
}

export const FIELD_DEFS: FieldDef[] = [
  // ---- 非価格・書き換え可 ----
  { key: 'contact_media', label: '予約経路', path: 'detail', kind: 'string', editable: true },
  { key: 'contact_plan', label: 'プラン', path: 'detail', kind: 'string', editable: true },
  { key: 'short_memo', label: 'ショートメモ', path: 'root', kind: 'string', editable: true },
  { key: 'comment', label: 'コメント', path: 'detail', kind: 'string', editable: true },

  // ---- 読み取り専用（抽出条件には使える） ----
  { key: 'contact_code', label: '予約番号', path: 'root', kind: 'string', editable: false },
  { key: 'customer_name', label: '予約者名', path: 'root', kind: 'string', editable: false },
  { key: 'customer_tel', label: '電話番号', path: 'root', kind: 'string', editable: false },
  { key: 'customer_email', label: 'メールアドレス', path: 'root', kind: 'string', editable: false },
  { key: 'car_type', label: '車種', path: 'root', kind: 'string', editable: false },
  { key: 'vehicle_id', label: '車両', path: 'root', kind: 'number', editable: false },
  { key: 'memo', label: 'メモ', path: 'detail', kind: 'string', editable: false },
  { key: 'pax', label: '大人人数', path: 'detail', kind: 'number', editable: false },
  { key: 'payment', label: '支払い済みフラグ', path: 'detail', kind: 'flag', editable: false },
  { key: 'license', label: '免許確認フラグ', path: 'detail', kind: 'flag', editable: false },

  // ---- 価格系: 抽出のみ。書き換えは第1弾では禁止 ----
  { key: 'sum_price', label: '合計金額', path: 'detail', kind: 'number', editable: false, price: true },
  { key: 'base_price', label: '基本料金', path: 'detail', kind: 'number', editable: false, price: true },
  { key: 'option_price', label: 'オプション料金', path: 'detail', kind: 'number', editable: false, price: true },
  { key: 'noc_price', label: 'NOC料金', path: 'detail', kind: 'number', editable: false, price: true },
  { key: 'cdw_price', label: 'CDW料金', path: 'detail', kind: 'number', editable: false, price: true },
];

const BY_KEY: Record<string, FieldDef> = FIELD_DEFS.reduce((acc, f) => {
  acc[f.key] = f;
  return acc;
}, {} as Record<string, FieldDef>);

export function getField(key: unknown): FieldDef | null {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(BY_KEY, key) ? BY_KEY[key] : null;
}

export function fieldLabel(key: string): string {
  return getField(key)?.label || key;
}

export const EDITABLE_FIELDS: FieldDef[] = FIELD_DEFS.filter((f) => f.editable);
